import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Zap } from 'lucide-react';
import type { LessonDay } from '../content/types';

type Visualizer = NonNullable<LessonDay['visualizer']>;
type VisualStep = Visualizer['steps'][number];

const networkPositions = {
  tree: [[50, 12], [27, 42], [73, 42], [13, 76], [38, 76], [62, 76], [87, 76]],
  graph: [[10, 50], [31, 20], [32, 78], [58, 28], [61, 76], [87, 49]],
} as const;

const networkEdges = {
  tree: [[0, 1], [0, 2], [1, 3], [1, 4], [2, 5], [2, 6]],
  graph: [[0, 1], [0, 2], [1, 3], [2, 3], [2, 4], [3, 5], [4, 5]],
} as const;

export function DataVisualizer({ visualizer }: { visualizer: Visualizer }) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => {
      if (step >= visualizer.steps.length - 1) setPlaying(false);
      else setStep(step + 1);
    }, 1850);
    return () => window.clearTimeout(timer);
  }, [playing, step, visualizer.steps.length]);

  const state = visualizer.steps[step];
  const go = (next: number) => {
    setStep(Math.max(0, Math.min(visualizer.steps.length - 1, next)));
    setPlaying(false);
  };

  return (
    <div className={`data-visualizer visualizer-v2 ${visualizer.kind}`}>
      <header className="visualizer-header">
        <div><span className={playing ? 'running' : ''} /><b>Live execution</b><small>{labelFor(visualizer.kind)}</small></div>
        {visualizer.kind === 'array' && <div className="target-chip"><small>TARGET</small><strong>{visualizer.target}</strong></div>}
      </header>
      <div className="visual-workspace">
        <div className="visual-stage">
          <motion.div className="operation-chip" key={state.operation} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}><Zap /> {state.operation}</motion.div>
          {visualizer.kind === 'tree' || visualizer.kind === 'graph'
            ? <NetworkStructure visualizer={visualizer} state={state} />
            : <LinearStructure visualizer={visualizer} state={state} />}
        </div>
        <aside className="visual-state">
          <span>STATE AT STEP {String(step + 1).padStart(2, '0')}</span>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }}>
              {Object.entries(state.state ?? {}).map(([name, value]) => <div key={name}><small>{name}</small><strong>{value}</strong></div>)}
            </motion.div>
          </AnimatePresence>
        </aside>
      </div>
      <div className="visual-narration">
        <span>{String(step + 1).padStart(2, '0')}</span>
        <AnimatePresence mode="wait"><motion.p key={step} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{state.label}</motion.p></AnimatePresence>
      </div>
      <footer className="visual-player">
        <button onClick={() => go(0)} aria-label="Restart"><RotateCcw /></button>
        <button onClick={() => go(step - 1)} disabled={step === 0} aria-label="Previous step"><ChevronLeft /></button>
        <button className="visual-play" onClick={() => { if (step === visualizer.steps.length - 1) setStep(0); setPlaying(!playing); }}>{playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />} {playing ? 'Pause' : 'Play animation'}</button>
        <button onClick={() => go(step + 1)} disabled={step === visualizer.steps.length - 1} aria-label="Next step"><ChevronRight /></button>
        <div className="visual-timeline">{visualizer.steps.map((item, index) => <button key={item.operation} className={index < step ? 'done' : index === step ? 'active' : ''} onClick={() => go(index)}><i>{index < step ? <Check /> : index + 1}</i><span>{item.operation}</span></button>)}</div>
      </footer>
    </div>
  );
}

function LinearStructure({ visualizer, state }: { visualizer: Visualizer; state: VisualStep }) {
  const finalMatch = visualizer.kind === 'array' && state.active.length > 1;
  return (
    <div className={`linear-structure ${finalMatch ? 'has-match' : ''}`}>
      {finalMatch && <motion.div className="match-arc" initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} />}
      {visualizer.values.map((value, index) => {
        const active = state.active.includes(index);
        const visited = state.visited?.includes(index);
        const pointers = state.pointers?.filter((pointer) => pointer.index === index) ?? [];
        const reversed = visualizer.kind === 'linked-list' && state.visited?.includes(index);
        return (
          <div className="visual-cell-wrap" key={`${value}-${index}`}>
            <small className="cell-index">{visualizer.kind === 'dp' ? `dp[${index}]` : index}</small>
            <motion.div className={`visual-cell ${active ? 'active' : ''} ${visited ? 'visited' : ''}`} animate={{ y: active ? -10 : 0, scale: active ? 1.07 : 1 }} transition={{ type: 'spring', stiffness: 330, damping: 22 }}>
              <AnimatePresence mode="wait"><motion.span key={value} initial={{ opacity: 0, scale: .7 }} animate={{ opacity: 1, scale: 1 }}>{value}</motion.span></AnimatePresence>
              {visited && !active && <i className="visited-mark"><Check /></i>}
            </motion.div>
            <div className="cell-pointers">{pointers.map((pointer) => <motion.b layoutId={`pointer-${pointer.label}`} key={pointer.label} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>{pointer.label}<i /></motion.b>)}</div>
            {visualizer.kind === 'linked-list' && index < visualizer.values.length - 1 && <motion.span className={`link-arrow ${reversed ? 'reversed' : ''}`} key={reversed ? 'left' : 'right'} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{reversed ? '←' : '→'}</motion.span>}
          </div>
        );
      })}
    </div>
  );
}

function NetworkStructure({ visualizer, state }: { visualizer: Visualizer; state: VisualStep }) {
  const kind = visualizer.kind === 'tree' ? 'tree' : 'graph';
  const positions = networkPositions[kind];
  const edges = networkEdges[kind];
  return (
    <div className="network-structure">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">{edges.map(([from, to]) => {
        const active = state.active.includes(from) && state.active.includes(to);
        const visited = state.visited?.includes(from) && state.visited?.includes(to);
        const [x1, y1] = positions[from] ?? [0, 0];
        const [x2, y2] = positions[to] ?? [0, 0];
        return <line key={`${from}-${to}`} className={active ? 'active' : visited ? 'visited' : ''} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}</svg>
      {visualizer.values.map((value, index) => {
        const active = state.active.includes(index);
        const visited = state.visited?.includes(index);
        const pointer = state.pointers?.find((item) => item.index === index);
        return <motion.div key={value} className={`network-node ${active ? 'active' : ''} ${visited ? 'visited' : ''}`} style={{ left: `${positions[index][0]}%`, top: `${positions[index][1]}%` }} animate={{ scale: active ? 1.16 : 1 }}><span>{value}</span>{pointer && <motion.b layoutId={`network-pointer-${pointer.label}`}>{pointer.label}</motion.b>}</motion.div>;
      })}
    </div>
  );
}

function labelFor(kind: Visualizer['kind']) {
  return ({ array: 'Array + hash map', stack: 'Stack memory', 'linked-list': 'Pointer memory', tree: 'Recursive call tree', graph: 'BFS frontier', dp: 'DP state table' })[kind];
}
